import { normalizeSpaces } from '../friendly';
import type { SampleSheetDraftRow } from './types';

export function normalizeAndTrim(value: string | null | undefined): string {
  if (value == null) {
    return '';
  }
  return normalizeSpaces(value).trim();
}

export function findCaseInsensitiveMatch<TContext = unknown>(
  value: string | null | undefined,
  candidates: readonly string[],
  getLabel?: (candidate: string, context?: TContext) => string,
  context?: TContext,
): string | null {
  const normalized = normalizeAndTrim(value);
  if (normalized === '') {
    const emptyMatch = candidates.find(candidate => normalizeAndTrim(candidate) === '');
    return emptyMatch ?? null;
  }
  const target = normalized.toLowerCase();
  for (const candidate of candidates) {
    const candidateNormalized = normalizeAndTrim(candidate);
    if (candidateNormalized.toLowerCase() === target) {
      return candidate;
    }
    if (getLabel) {
      const labelNormalized = normalizeAndTrim(getLabel(candidate, context));
      if (labelNormalized && labelNormalized.toLowerCase() === target) {
        return candidate;
      }
    }
  }
  return null;
}

export function findUniqueSubstringMatch<TContext = unknown>(
  value: string,
  candidates: readonly string[],
  getLabel: (candidate: string, context?: TContext) => string,
  context?: TContext,
): string | null {
  const normalizedValue = normalizeAndTrim(value);
  if (normalizedValue === '') {
    return null;
  }
  const target = normalizedValue.toLowerCase();
  const matches: string[] = [];
  candidates.forEach(candidate => {
    const candidateNormalized = normalizeAndTrim(candidate).toLowerCase();
    if (candidateNormalized && candidateNormalized.includes(target)) {
      matches.push(candidate);
      return;
    }
    const label = normalizeAndTrim(getLabel(candidate, context)).toLowerCase();
    if (label && label.includes(target)) {
      matches.push(candidate);
    }
  });
  return matches.length === 1 ? matches[0] : null;
}

export type DropdownSnapMode = 'exact' | 'unique-substring';

export type DropdownDomainLookup = {
  options: readonly string[];
  getLabel?: (candidate: string, row: SampleSheetDraftRow) => string;
  mode?: DropdownSnapMode;
};

export type DropdownDomains = Partial<Record<keyof SampleSheetDraftRow, DropdownDomainLookup>>;

export function snapDropdownValue(
  field: keyof SampleSheetDraftRow,
  raw: unknown,
  row: SampleSheetDraftRow,
  domains: DropdownDomains,
): string | null {
  const domain = domains[field];
  if (!domain) {
    return typeof raw === 'string' ? normalizeAndTrim(raw) : null;
  }

  const value = typeof raw === 'string' ? normalizeAndTrim(raw) : '';
  if (value === '') {
    return null;
  }

  const { options, mode = 'exact', getLabel } = domain;
  const labelResolver = getLabel ? (candidate: string, context?: SampleSheetDraftRow) => getLabel(candidate, context ?? row) : undefined;
  const canonical = findCaseInsensitiveMatch(value, options, labelResolver, row);
  if (canonical) {
    return canonical;
  }
  if (mode === 'unique-substring') {
    const unique = findUniqueSubstringMatch(value, options, labelResolver ?? ((candidate: string) => candidate), row);
    if (unique) {
      const resolved = findCaseInsensitiveMatch(unique, options, labelResolver, row);
      return resolved ?? unique;
    }
  }
  return value;
}
